import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { api } from '../lib/api';
import type { ChangeMarks } from '../lib/types';

/**
 * Jeden lacný dopyt namiesto desiatich drahých.
 *
 * Appka sa predtým pýtala na drahé veci dookola, aj keď sa celé hodiny nič
 * nedialo: samotný zoznam projektov stál 154 prečítaných riadkov a volal sa
 * dvetisíckrát denne. Denný limit databázy sa tým minul dvakrát a appka
 * prestala ísť celá.
 *
 * Odteraz sa v pravidelnom intervale pýtame len na najvyššie id z tabuliek
 * (tri prečítané riadky) a drahé dotazy obnovíme až vtedy, keď sa niektoré
 * číslo pohne. Keď je pokoj, nestojí to prakticky nič.
 *
 * Vedľajší účinok je, že je to aj RÝCHLEJŠIE: cudzia zmena sa prejaví do
 * pätnástich sekúnd namiesto tridsiatich, lebo lacnú otázku si môžeme dovoliť
 * klásť častejšie než drahú.
 */

/** Ako často sa pýtame „pribudlo niečo?". Lacné, takže smie byť nakrátko. */
export const INTERVAL_MS = 15_000;

/**
 * Čo obnoviť, keď sa daná známka pohne.
 *
 * Kľúče sú predpony — react-query zasiahne všetky varianty pod nimi, takže
 * `['projects']` pokryje aj `['projects','list',false]` aj `['projects','detail']`.
 */
export const DOTKNUTE: Record<keyof ChangeMarks, string[][]> = {
  // Nástenka a všetko, čo číta z knihy udalostí. „Čaká na mňa“ tiež:
  // komentár aj presun do Testovania zapíšu udalosť.
  events: [['events'], ['waiting'], ['guest-permissions']],
  // Nová správa mení aj počet neprečítaných na zozname projektov. Anketa aj
  // zmienka v chate sú správa, takže aj „Čaká na mňa“.
  messages: [['messages'], ['dm'], ['projects'], ['waiting']],
  // Spustené a ukončené sedenie mení, kto na čom robí.
  sessions: [['sessions'], ['projects'], ['recap']],
  tasks: [['tasks']],
  projects: [['projects']],
  notes: [['notes']],
  expenses: [['expenses']],
  inbox: [['inbox']],
};

export const useChangeMarks = (enabled: boolean): void => {
  const queryClient = useQueryClient();
  const predosle = useRef<ChangeMarks | null>(null);

  const { data } = useQuery({
    queryKey: ['changes'],
    queryFn: () => api.changes(),
    enabled,
    refetchInterval: INTERVAL_MS,
    // Bez tohto by sa pri každom prepnutí obrazovky pýtal nanovo. Je to lacné,
    // ale zbytočné — interval beží ďalej.
    staleTime: INTERVAL_MS,
  });

  useEffect(() => {
    if (!data) {
      return;
    }
    const predchadzajuce = predosle.current;
    predosle.current = data;

    // Prvá odpoveď po prihlásení nie je zmena, len východisko. Bez tohto by
    // sa hneď po otvorení appky obnovilo všetko naraz — teda presne to drahé,
    // čomu sa vyhýbame.
    if (!predchadzajuce) {
      return;
    }

    for (const kluc of Object.keys(DOTKNUTE) as Array<keyof ChangeMarks>) {
      // Iba rast. Keby sa číslo zmenšilo (zmazaný riadok s najvyšším id),
      // nie je to nič, čo by sa dalo dotiahnuť obnovením práve tejto skupiny.
      if (data[kluc] > predchadzajuce[kluc]) {
        for (const queryKey of DOTKNUTE[kluc]) {
          void queryClient.invalidateQueries({ queryKey });
        }
      }
    }
  }, [data, queryClient]);
};
